import React, { useState, useEffect } from 'react';
import { Globe, Mail, Phone, Calendar, HelpCircle } from 'lucide-react';
import { useTranslation } from "../../../contexts/LanguageContext";
import { useAuth } from "../../../App";

const MerchantDetailsTab = ({ merchant }) => {
  const { t } = useTranslation();
  const { httpClient } = useAuth();
  const [affiliateLink, setAffiliateLink] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchAffiliateLink = async () => {
      if (!merchant?.id) return;
      
      setLoading(true);
      try {
        // Use httpClient which handles the base URL
        const params = new URLSearchParams({
          merchant_id: merchant.id,
          link_type: 'textlink',
          plan_id: 'null'
        });
        
        const response = await httpClient.request(
          `/api/merchant-affiliate-links?${params}`
        );
        
        if (response.ok) {
          const links = await response.json();
          if (links.length > 0) {
            setAffiliateLink(links[0]);
          }
        }
      } catch (error) {
        console.error('Error fetching affiliate link:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchAffiliateLink();
  }, [merchant, httpClient]);

  const formatDate = (dateString) => {
    if (!dateString || dateString === "No Data") return t('common.na') || "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const handleWebsiteClick = async (e) => {
    if (affiliateLink) {
      // Track the click using httpClient
      try {
        await httpClient.request('/api/track-affiliate-click', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            affiliate_link_id: affiliateLink._id,
            merchant_id: merchant.id,
            plan_id: null
          }),
        });
      } catch (error) {
        console.error('Error tracking affiliate click:', error);
      }
      
      // Open the affiliate link in a new tab
      window.open(affiliateLink.url, '_blank');
      e.preventDefault();
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-[#253985] flex items-center">
          <Globe className="h-5 w-5 mr-2" />
          {t('merchant.detailsTab.companyInformation') || "Company Information"}
        </h2>
        <div className="space-y-3">
          <div className="flex items-start">
            <Globe className="h-5 w-5 text-[#253985] mr-3 mt-0.5" />
            <div>
              <p className="text-sm text-gray-500">{t('merchant.common.website') || "Website"}</p>
              {loading ? (
                <div className="h-5 w-40 bg-gray-200 animate-pulse rounded"></div>
              ) : affiliateLink ? (
                <a 
                  href={affiliateLink.url} 
                  onClick={handleWebsiteClick}
                  rel="sponsored noopener noreferrer"
                  target="_blank"
                  className="text-[#253985] hover:underline"
                >
                  {merchant.website}
                </a>
              ) : (
                <a 
                  href={merchant.website} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[#253985] hover:underline"
                >
                  {merchant.website}
                </a>
              )}
            </div>
          </div>
          <div className="flex items-start">
            <Mail className="h-5 w-5 text-[#253985] mr-3 mt-0.5" />
            <div>
              <p className="text-sm text-gray-500">{t('merchant.common.contactEmail') || "Email"}</p>
              <a 
                href={`mailto:${merchant.email}`}
                className="text-[#253985] hover:underline"
              >
                {merchant.email}
              </a>
            </div>
          </div>
          <div className="flex items-start">
            <Phone className="h-5 w-5 text-[#253985] mr-3 mt-0.5" />
            <div>
              <p className="text-sm text-gray-500">{t('merchant.common.phone') || "Phone"}</p>
              <p className="text-[#253985]">{merchant.phone}</p>
            </div>
          </div>
          <div className="flex items-start">
            <Calendar className="h-5 w-5 text-[#253985] mr-3 mt-0.5" />
            <div>
              <p className="text-sm text-gray-500">{t('merchant.common.founded') || "Founded"}</p>
              <p className="text-[#253985]">{formatDate(merchant.founded)}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-[#253985] flex items-center">
          <HelpCircle className="h-5 w-5 mr-2" />
          {t('merchant.detailsTab.about') || "About"}
        </h2>
        <p className="text-gray-700">
          {merchant.description}
        </p>
        <div className="mt-4">
          <span className="inline-block bg-blue-100 text-[#253985] px-3 py-1 rounded-full text-sm font-medium">
            {merchant.category}
          </span>
        </div>
      </div>
    </div>
  );
};

export default MerchantDetailsTab;